import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { FormControl, FormGroup, FormLabel } from "react-bootstrap";
import ColorPicker from "./../../common/color-picker/ColorPicker";
import i18n from "i18next";
import FormCheckButtonFormGroup from "../radio_button_form_group/RadioButtonFormGroup";
import ToggleSwitch from "@trendmicro/react-toggle-switch";
import "@trendmicro/react-toggle-switch/dist/react-toggle-switch.css";
import "./_gridConfigurator.scss";

const ContainerConfigurator = ({
  id,
  parentId,
  container,
  onColsChanged,
  onRowsChanged,
  sortableProps,
  onSortablePropsChanged,
  onSortableContainerResized,
}) => {
  const alignment = useMemo(
    () => container.style?.textAlign ?? "center",
    [container.style?.textAlign]
  );

  const height = useMemo(() => sortableProps.height, [sortableProps.height]);

  const handleColsChanged = useCallback(
    (dist) => onColsChanged(id, parentId, dist, container.children),
    [id, parentId, onColsChanged, container.children]
  );

  const handleRowsChanged = useCallback(
    (index, dist) => onRowsChanged(id, parentId, index, dist, container.children),
    [id, parentId, onRowsChanged, container.children]
  );

  const handleSortableContainerResized = useCallback(
    (newHeight) => onSortableContainerResized(id, parentId, newHeight),
    [id, parentId, onSortableContainerResized]
  );

  const handleSortablePropsChanged = useCallback(
    (property, value) => onSortablePropsChanged(id, parentId, property, value),
    [id, parentId, onSortablePropsChanged]
  );

  return (
    <div className="gc-root">
      <h4 className="sortableToolbarTitle">{i18n.t("Structure")}</h4>

      {/* Height */}
      <FormGroup>
        <div className="gc-label-row">
          <FormLabel>{i18n.t("Height")}</FormLabel>
          <div className="gc-toggle-row">
            <span className="gc-toggle-label">auto</span>
            <div className="custom-toggle-wrapper">
              <ToggleSwitch
                checked={height === "auto"}
                onChange={() => {
                  const current = height === "auto";
                  const newHeight = current
                    ? parseFloat(document.getElementById(id)?.clientHeight, 10)
                    : "auto";
                  handleSortableContainerResized(newHeight);
                }}
              />
            </div>
          </div>
        </div>
        <FormControl
          type={height === "auto" ? "text" : "number"}
          disabled={height === "auto"}
          value={height === "auto" ? "auto" : parseFloat(height, 10)}
          min={1}
          step={1}
          onChange={(e) => handleSortableContainerResized(e.target.value)}
        />
      </FormGroup>

      {/* Alignment */}
      <FormCheckButtonFormGroup
        title={i18n.t("Alignment")}
        options={["left", "center", "right"]}
        selected={alignment}
        click={(option) => handleSortablePropsChanged("textAlign", option)}
        tooltips={[
          i18n.t("messages.align_left"),
          i18n.t("messages.align_center"),
          i18n.t("messages.align_right"),
        ]}
        icons={["format_align_left", "format_align_center", "format_align_right"]}
      />

      {/* Grid preview — hidden
      <div className="configurator">
        {container.cols.map((item, index) => (
          <div
            className="gc_columns"
            key={index}
            style={{ width: container.colDistribution[index] + "%" }}
          >
            {item.map((it, ind) => (
              <div className="gc_rows" key={ind} style={{ height: it + "%" }} />
            ))}
          </div>
        ))}
      </div>
      */}

      {/* Columns count + rows count — side by side */}
      <div className="gc-two-col">
        {/* <FormGroup>
          <FormLabel>{i18n.t("col_number")}</FormLabel>
          <FormControl
            type="number"
            value={container.colDistribution.length}
            min={1}
            step={1}
            onChange={(e) => {
              const arr = Number(e.target.value);
              const dist = Array.from({ length: arr }, () =>
                Math.round((100 / arr) * 100) / 100
              );
              handleColsChanged(dist);
            }}
          />
        </FormGroup> */}
      </div>

      <h4 className="sortableToolbarTitle">{i18n.t("Style")}</h4>

      {/* Padding */}
      <FormGroup>
        <FormLabel>{i18n.t("styles.padding") + " (px)"}</FormLabel>
        <FormControl
          type="number"
          value={container.style ? parseFloat(container.style.padding, 10) : 0}
          min={0}
          max={100}
          onChange={(e) => handleSortablePropsChanged("padding", e.target.value + "px")}
        />
      </FormGroup>

      {/* Border color */}
      <FormGroup>
        <FormLabel>{i18n.t("styles.border_color")}</FormLabel>
        <ColorPicker
          value={container.style ? container.style.borderColor : "#ffffff"}
          onChange={(e) => handleSortablePropsChanged("borderColor", e.color)}
        />
      </FormGroup>

      {/* Border size */}
      <FormGroup>
        <FormLabel>{i18n.t("styles.border_size")}</FormLabel>
        <FormControl
          type="number"
          value={container.style ? parseFloat(container.style.borderWidth, 10) : 0}
          min={0}
          onChange={(e) => handleSortablePropsChanged("borderWidth", e.target.value + "px")}
        />
      </FormGroup>

      {/* Border style */}
      <FormGroup>
        <FormLabel>{i18n.t("styles.border_style") || "Border style"}</FormLabel>
        <select
          className="form-control"
          value={container.style ? container.style.borderStyle : "none"}
          onChange={(e) => handleSortablePropsChanged("borderStyle", e.target.value)}
        >
          {[
            "none",
            "hidden",
            "dotted",
            "dashed",
            "solid",
            "double",
            "groove",
            "ridge",
            "inset",
            "outset",
            "initial",
            "inherit",
          ].map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </FormGroup>

      {/* Opacity */}
      <FormGroup>
        <div className="gc-label-row">
          <FormLabel>{i18n.t("styles.opacity")}</FormLabel>
          <span className="rangeOutput">{container.style ? container.style.opacity : 1}</span>
        </div>
        <FormControl
          type="range"
          className="rangeInput"
          value={container.style ? container.style.opacity : 1}
          min={0}
          step={0.05}
          max={1}
          onChange={(e) => handleSortablePropsChanged("opacity", e.target.value)}
        />
      </FormGroup>
    </div>
  );
};

ContainerConfigurator.propTypes = {
  id: PropTypes.string,
  parentId: PropTypes.any.isRequired,
  container: PropTypes.any.isRequired,
  onColsChanged: PropTypes.func.isRequired,
  onRowsChanged: PropTypes.func.isRequired,
  sortableProps: PropTypes.object,
  onSortablePropsChanged: PropTypes.func.isRequired,
  onSortableContainerResized: PropTypes.func.isRequired,
};

export default ContainerConfigurator;
